import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  FileFilterModel,
  useCurrentWorkspace,
  useFileFilter,
  useIsFeatureEnabled,
  WorkspaceType,
} from '@gripp/shared-logic';
import { Colors, SlideLayout } from '@gripp/shared-ui';
import { useMemo } from 'react';
import { StyleProp, StyleSheet, Text, View } from 'react-native';
import { ModalButton } from '../../shared/components/modalButton';
import { FileAssetsForm } from './FileAssetsForm';
import { FileAssignmentForm } from './fileAssignmentForm';
import { Files as FilesBaseOld } from './files-old';
import { FilesFilters } from './filesFilters';
import { FilesList } from './filesList';

export const Files = () => {
  const isCropCircleEnable = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnable) {
    return <FilesBase />;
  }

  return <FilesBaseOld />;
};

export const FilesBase = ({
  rightColumn,
  viewMode,
}: {
  rightColumn?: any;
  viewMode?: any;
}) => {
  const { data: fileFilter } = useFileFilter();
  const currentWorkspace = useCurrentWorkspace();
  const impersonatingWorkspace = useCurrentWorkspace(true);

  const showCheckboxColumn = useMemo(
    () => !!impersonatingWorkspace,
    [impersonatingWorkspace]
  );

  const filter = useMemo(() => {
    const filter: FileFilterModel = fileFilter ?? new FileFilterModel();
    filter.workspaceId = currentWorkspace?.id;
    return filter.getGraphQLFilter();
  }, [fileFilter]);

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const CountButton = (data: any) => {
    const isProducerWorkspace =
      currentWorkspace?.type === WorkspaceType.Producer;

    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name={data.iconName}
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.count)}
          </View>
        }
        modalComponent={({ onComplete }: { onComplete: () => void }) => {
          if (isProducerWorkspace) {
            return (
              <FileAssetsForm
                fileId={data.fileId}
                title={data.name}
                showCheckboxColumn={showCheckboxColumn}
                onComplete={onComplete}
              />
            );
          }

          return (
            <FileAssignmentForm
              fileId={data.fileId}
              title={data.name}
              showCheckboxColumn={showCheckboxColumn}
              // if user clicked addons count open addons tab first, else products
              initialTab={data.countType === 'addon' ? 'addons' : 'products'}
              onComplete={onComplete}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const filterComponent = <FilesFilters />;

  const columnComponent = (
    <View style={styles.container}>
      <FilesList
        filter={filter}
        showCheckboxColumn={showCheckboxColumn}
        countButton={CountButton}
        isProducerWorkspace={currentWorkspace?.type === WorkspaceType.Producer}
      />
    </View>
  );

  return (
    <SlideLayout
      leftColumn={{
        title: 'file.list.files',
        mainComponent: columnComponent,
        headerComponent: filterComponent,
      }}
      rightColumn={rightColumn}
      viewMode={viewMode}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
});
